import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import moment from 'moment'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {forwardRef, useImperativeHandle, useMemo, useRef} from 'react'
import Avatar from '../../../assets/images/avatar.jpg'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../../utils/BlockScrollsUtils'

// import {ReactComponent as DuoAddress} from '../../../assets/icons/DuoAddress.svg'
import {Divider} from 'primereact/divider'
import {InputTextarea} from 'primereact/inputtextarea'
import {ReactComponent as DuoAddress2} from '../../../assets/icons/DuoAddress2.svg'
import {
    ReactComponent as DuoCalendarCheck,
    ReactComponent as DuoDateAppointment,
} from '../../../assets/icons/DuoCalendarCheck.svg'
import {ReactComponent as DuoMail} from '../../../assets/icons/DuoMail.svg'
import {ReactComponent as DuoMale} from '../../../assets/icons/DuoMale.svg'
import {ReactComponent as DuoPhone} from '../../../assets/icons/DuoPhone.svg'
import {ReactComponent as DuoTypeAppointment} from '../../../assets/icons/DuoTagPlus.svg'
import {ReactComponent as FaUserMd} from '../../../assets/icons/stethoscope.svg'
import SendMessageForm from '../../message/SendMessageForm'
import IconWrapper from '../../new-table/IconWrapper'
import './style.scss'

const ReservationDialog = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateDatatable, translateCommons, translateKeys} =
        TranslateUtils()

    const visible = useSignal(false)
    const appointmentData = useSignal({})
    const isShowMore = useSignal(false)

    const sendMessageRef = useRef(null)

    const handleShowForm = value => {
        blockOverflownScroll()

        if (value) {
            appointmentData.value = value
            visible.value = true
        }
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        appointmentData.value = {}
        visible.value = false
    }

    const patientInformation = useMemo(() => {
        return appointmentData.value?.userInfo || {}
    }, [appointmentData.value?.userInfo])

    const renderHeaderTemplate = () => {
        return (
            <div className="w-full text-xs md:text-sm lg:text-lg">
                <div className="flex flex-wrap-reverse align-items-center gap-3">
                    <span className="mr-2 font-normal flex align-items-center">
                        {translateCommons('Reservation of')}
                        <div className="font-bold mx-2">
                            {patientInformation?.fullName || '-'}
                        </div>
                    </span>
                    <span className="flex align-items-center">
                        <span>&bull;</span>
                        <i className="pi pi-desktop mx-2"></i>
                        <p>{translateCommons('MANUAL APPOINTMENT')}</p>
                    </span>
                </div>
                <hr />
            </div>
        )
    }

    const renderPatientInformation = () => {
        return (
            <div className="text-xs md:text-sm lg:text-base">
                {/* Header */}
                <div className="flex gap-3 mb-4">
                    <img
                        alt="avatar"
                        src={Avatar}
                        className="p-2 bg-white border-circle shadow-sm"
                        style={{width: '100px', height: '100px'}}
                    />
                    <div className="flex flex-column gap-1">
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="m-0 font-semibold text-base lg:text-lg">
                                {patientInformation?.fullName || '-'}
                            </h4>
                            <DuoMale
                                className="text-blue-500"
                                height={16}
                                width={16}
                            />
                        </div>
                        <div className="flex items-center font-semibold text-blue-600">
                            <DuoCalendarCheck
                                className="mr-2"
                                height={17}
                                width={17}
                            />
                            <span>{patientInformation?.dob || '-'}</span>
                        </div>
                        <div> </div>
                        <div className="flex items-center font-semibold text-orange-600">
                            <DuoMail className="mr-2" height={17} width={17} />
                            <span>{patientInformation?.email || '-'}</span>
                        </div>
                        <div className="flex items-center text-cyan-600">
                            <DuoPhone className="mr-2" height={17} width={17} />
                            <span>
                                {patientInformation?.phone &&
                                patientInformation?.phone?.length > 9
                                    ? patientInformation?.phone
                                    : '-'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Contact Info */}
                <div className="grid gap-3 text-gray-700">
                    <div className="flex items-center">
                        <DuoAddress2 className="mr-2" height={17} width={17} />
                        <span>{patientInformation?.address || '-'}</span>
                    </div>
                </div>
            </div>
        )
    }
    const renderAppointmentInformation = () => {
        const appt = appointmentData.value || {}

        const infoItems = [
            {
                key: 'type',
                label: 'Type',
                value: appt.appointmentType,
                icon: (
                    <DuoTypeAppointment
                        className="text-purple-700"
                        style={{width: 25, height: 25}}
                    />
                ),
            },
            {
                key: 'doctor',
                label: 'Doctor',
                value: appt.ownerName,
                icon: (
                    <FaUserMd
                        className="text-blue-500"
                        style={{width: 25, height: 25}}
                    />
                ),
            },
            {
                key: 'dateAndTime',
                label: 'Date & Time',
                value: appt.dateAndTime
                    ? moment(appt.dateAndTime).format('MMM DD YYYY h:mm A')
                    : '-',
                icon: (
                    <DuoDateAppointment
                        className="text-orange-500"
                        style={{width: 25, height: 25}}
                    />
                ),
            },
        ]

        return (
            <div className="grid text-xs md:text-sm lg:text-base">
                {/* Các mục cơ bản */}
                {infoItems.map(item => (
                    <div
                        key={item.key}
                        className="col-4 flex align-items-center gap-2 mb-3">
                        <div className="bg-gray-50 border-1 border-gray-100 border-round-lg p-1">
                            {' '}
                            {item.icon}
                        </div>

                        <div>
                            <h6 className="m-0 text-500 uppercase text-sm">
                                {item.label}
                            </h6>
                            <p className="m-0 font-semibold">
                                {item.value || '-'}
                            </p>
                        </div>
                    </div>
                ))}

                {/* Diagnose */}
                <div className="col-12 m-0 p-0 mb-3">
                    <div className="flex align-items-center gap-2 mb-2">
                        {/* <DuoSick
                            className="text-pink-700"
                            style={{width: 25, height: 25}}
                        /> */}
                        <h6 className="m-0 text-500 uppercase text-sm">
                            Diagnose
                        </h6>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {patientInformation?.pathways &&
                            Object.values(patientInformation.pathways).map(
                                p => (
                                    <div
                                        key={p?.name}
                                        className="bg-white border-round-lg border-1 border-gray-200 p-2">
                                        <IconWrapper
                                            id={p?.name + 'filterBy'}
                                            content={p?.name}
                                            icon={
                                                <img
                                                    src={p?.icon}
                                                    alt={p?.name}
                                                    style={{
                                                        width: 25,
                                                        height: 25,
                                                    }}
                                                />
                                            }
                                            tooltip={false}
                                        />
                                    </div>
                                ),
                            )}
                    </div>
                </div>

                {/* Description */}
                <div className="col-12 m-0 p-0 mb-2">
                    <div className="flex align-items-center gap-2 mb-2">
                        <h6 className="m-0 text-500 uppercase text-sm">
                            Description
                        </h6>
                    </div>
                    <InputTextarea
                        rows={5}
                        className="w-full"
                        value={appt.description}
                        disabled={true}
                    />
                </div>
            </div>
        )
    }

    return (
        <Dialog
            header={renderHeaderTemplate()}
            headerClassName="pb-0 bg-white text-color"
            visible={visible.value}
            position={'right'}
            style={{width: '50vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}
            onHide={handleHideForm}
            draggable={false}
            resizable={false}>
            {renderPatientInformation()}
            <Divider type="dashed" />
            <h6 className="font-bold text-blue-500">Appointment Information</h6>
            {renderAppointmentInformation()}

            <Button
                icon="pi pi-bell"
                label={translateCommons('Send notification')}
                className="w-full "
                onClick={() =>
                    sendMessageRef.current.showForm(patientInformation)
                }
            />
            {/* <div className="my-2">
                <Button
                    label={translateDatatable('Close').toUpperCase()}
                    icon="pi pi-times"
                    className="col-12 p-button-lg p-button-outlined font-bold mb-2"
                    onClick={handleHideForm}
                />
            </div> */}

            {/* Form Ref */}
            <SendMessageForm ref={sendMessageRef} />
        </Dialog>
    )
})

export default ReservationDialog
